import { Form } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import Box from 'components/Box';
import Modal from 'components/Modal';
import Text from 'components/Text';
import { PeriodValues } from 'features/appointment/components/InputPeriod';
import appointmentApis from 'features/appointment/services/apis';
import { APPOINTMENT_SOURCE_TYPE } from 'features/appointment/services/constants';
import { aptUIActions } from 'features/appointment/services/reducers';
import appointmentSelectors from 'features/appointment/services/selectors';
import { IBodyApiMakeAppointment } from 'features/appointment/services/types/api';
import { IAppointmentDraftPopupData, IAppointmentItemData } from 'features/appointment/services/types/appointment';
import { DATE_FORMAT } from 'features/appointment/utils/format';
import moment, { Moment } from 'moment';
import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { IResponseDataBody } from 'services/response';
import shopSelectors from 'services/shop/selectors';
import { ICustomerItemData } from 'services/shop/types/customer';
import { IStaffItemData } from 'services/shop/types/staff';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { verifyPinCode } from 'widgets/RequestAdminLocalPW/Callback';
import CalendarPicker from '../../QuickBooking/CalendarPicker';
import StartTimes, { useStartTimesRef } from '../../QuickBooking/StartTimes';
import BlockHourStaffs, { useBlockHourStaffsRef } from './BlockHourStaffs';
import Repeat from './Repeat';
import { useServiceHandlerRef } from './ServiceHandler';
import StaffChoose from './StaffChoose';
import StaffHandler from './StaffHandler';
import { IQuickBookingV2ServiceItem } from '../../QuickBookingV2/type';
import { CustomerSearchInput } from '../../QuickBookingV2/Customer';
import { useInquiryDayOffsWithStaff } from '../../QuickBookingV2/hooks';
type IFormValues = {
  staff: IStaffItemData | null;
  customer: ICustomerItemData | null;
  repeat: number;
  note: string;
  services: { label: string, value: string, id: string }[];
  date: Moment;
  startTime: string;
  requestStaff?: boolean;
  aptStatus?: 'request' | 'none-request' | 'online';
};
type NewAppointmentVersionV2Ref = {
  open: (data: IAppointmentDraftPopupData) => void;
  close: () => void;
}; export const useNewAppointmentVersionV2Ref = () => useRef<NewAppointmentVersionV2Ref>(null);
export const newAppointmentVersionV2Ref = React.createRef<NewAppointmentVersionV2Ref>();
const NewAppointmentVersionV2 = forwardRef<NewAppointmentVersionV2Ref, any>((props, ref) => {
  const allSetting = shopSelectors.data.allSetting();
  const dispatch = useAppDispatch();
  const [isVisible, setVisible] = useState(false);
  const staffList = appointmentSelectors.getListStaffAvailableBooking();
  const [form] = Form.useForm();
  const startTimesRef = useStartTimesRef();
  const serviceRef = useServiceHandlerRef();
  const blockHourStaffsRef = useBlockHourStaffsRef();
  const setLoadingPage = useSetLoadingPage();
  const staffVerifiedPinCode = appointmentSelectors.staffVerifiedPinCode();
  const staffs = useMemo(() => staffList.filter(o => !o.anyBody), [staffList]);

  const cacheDate = useRef({
    startTime: '',
    endTime: '',
    staffId: '',
  });

  const [_dateOffState, fetchDateOff] = useInquiryDayOffsWithStaff({
    onStart: ({ endTime, startTime, staffId }) => {
      cacheDate.current.startTime = startTime;
      cacheDate.current.endTime = endTime;
      cacheDate.current.staffId = staffId || '';
    },
    onDone(data) {
      form.setFieldValue('dayOffs', data);
    },
  });

  const handleFetch = (date: Moment, staffId?: string) => {
    const now = date;
    const startTime = now.clone().subtract(1, 'month').startOf('month').format('MM-DD-YYYY');
    const endTime = now.clone().add(1, 'month').endOf('month').format('MM-DD-YYYY');
    if (cacheDate.current.startTime === startTime && cacheDate.current.endTime === endTime && cacheDate.current.staffId === staffId) return;
    fetchDateOff({ startTime, endTime, staffId: staffId || '' });
  };

  const onMonthChange = (now: Moment) => {
    const startTime = now.clone().subtract(1, 'month').startOf('month').format('MM-DD-YYYY');
    const endTime = now.clone().add(1, 'month').endOf('month').format('MM-DD-YYYY');
    fetchDateOff({ startTime, endTime, staffId: cacheDate.current.staffId || '' });
  };

  const handleClose = () => setVisible(false);

  const handleOpen = (_data: IAppointmentDraftPopupData) => {
    const callback = () => {
      setVisible(true);
      const anyone = staffList.find(o => o.anyBody);
      const staffData: IStaffItemData | undefined = staffList.find(o => o.id === _data.staff?.id);
      form.setFieldValue('staff', staffData || anyone || null);
      const date = _data?.colTime ? moment(_data.colTime) : moment();
      form.setFieldValue('date', date);
      form.setFieldValue('startTime', date.format('hh:mm A'));

      setTimeout(() => {
        onPickDate(date);
        serviceRef.current?.fetch(staffData?.id || '');
      }, 100);
    };

    if (allSetting?.enableVerifyBooking && !_data?.verifiedPinCode) {
      return verifyPinCode(() => callback(), true);
    }
    callback();
  };

  const onPickDate = async (value: Moment) => {
    if (!value) return;
    const staffId = form.getFieldValue('staff')?.id || '';
    handleFetch(value, staffId);
    const startDate: string = form.getFieldValue('startTime');
    form.setFieldValue('time-slots', []);
    blockHourStaffsRef.current?.fetch(value?.format(DATE_FORMAT), value?.format(DATE_FORMAT));
    startTimesRef.current?.setLoading(true);
    try {
      const resData: IResponseDataBody<string[]> = await appointmentApis.getStaffTimeSlot(staffId || '', value?.format(DATE_FORMAT) || '');
      const list = resData.data.data;

      if (list) {
        const data: string[] = list.map(d => moment(d, 'HH:mm').format('hh:mm A'));
        form.setFieldValue('time-slots', data);
        if (startDate) {
          form.setFieldValue('startTime', data.includes(startDate) ? startDate : '');
        }
      } else {
        form.setFieldValue('time-slots', []);
      }
    } catch (error) { }
    finally {
      startTimesRef.current?.setLoading(false);
    }
  };

  const onPickStaff = (staff: IStaffItemData) => {
    serviceRef.current?.fetch(staff.id);
    handleFetch(form.getFieldValue('date'), staff.id);
    setTimeout(() => onPickDate(form.getFieldValue('date')), 100);
  };

  const handleDoneConfirm = () => {
    setVisible(false);
  };

  const onFinishForm = async (values: any) => {
    const body = values as IFormValues;
    setLoadingPage(true);
    const timeStart = moment(`${body.date.format('MM-DD-YYYY')} ${body.startTime}`, 'MM-DD-YYYY hh:mm A');
    const payload: IBodyApiMakeAppointment = {
      bookingType: APPOINTMENT_SOURCE_TYPE.POS,
      customerId: body.customer?.id || '',
      serviceId: body.services.map(o => o.id),
      staffId: body.staff ? body.staff.id : '',
      startTime: timeStart?.format('MM/DD/YYYY HH:mm:ss'),
      repeat: body.repeat,
      note: body.note,
      requestStaff: body.aptStatus === 'request',
      bookingOnline: body.aptStatus === 'online',
    };
    if (staffVerifiedPinCode?.id) {
      payload.createdBy = staffVerifiedPinCode.id;
    }
    try {
      const res: IResponseDataBody<IAppointmentItemData[]> = await appointmentApis.makeNewAppointment(payload);
      if (res?.data?.data) {
        dispatch(aptUIActions.syncAppointments(res?.data?.data));
        handleDoneConfirm();
      }
    } catch (error) { }
    finally {
      setLoadingPage(false);
    }
  };
  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  if (!isVisible) return null;
  return (
    <Modal
      visible={isVisible}
      v2 noneBodyStyle
      modalTitle='new appointment'
      okTitle={'Book'}
      width={'auto'}
      onClose={handleDoneConfirm}
      className={'modal-add-new-appointment modal-max-height-unset modal-non-opacity'}
      hiddenHeader
      onSubmit={() => form.submit()}
    >
      <FormStyled onFinish={onFinishForm} initialValues={{ repeat: PeriodValues.NO_REPEAT, services: [], aptStatus: 'request' }} form={form} layout='vertical'>
        <Box display='flex' alignItems='center'>
          <Box display='flex' alignSelf='stretch'>
            <Form.Item noStyle name='staff'>
              <StaffChoose onPickStaff={onPickStaff} staffs={staffs} />
            </Form.Item>
          </Box>
          <Container>
            <HeaderModal><span>BOOK BY APPOINTMENT</span></HeaderModal>
            <Content >
              <DetailContainer>
                <div className='section-box'>
                  <Form.Item label={'Customer'} name={'customer'} rules={[
                    ({ getFieldValue }) => ({
                      async validator() {
                        const customer: IQuickBookingV2ServiceItem =
                          getFieldValue('customer');
                        if (!customer || customer.id === '_unknownCustomerId') {
                          return Promise.reject('Please choose customer');
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}>
                    <CustomerSearchInput
                      v2NewApt
                      preventUnknown
                      autoOpenAddWhenNotFound
                      v2 />
                  </Form.Item>
                </div>
                <div className='section-box'>
                  <Form.Item label={'Repeat'} name={'repeat'}>
                    <Repeat />
                  </Form.Item>
                </div>
                <Form.Item noStyle name={'staff'}>
                  <StaffHandler serviceRef={serviceRef} />
                </Form.Item>
                <div className="section-box" style={{ border: 'none', padding: '16px' }}>
                  <Form.Item noStyle name={'note'}>
                    <TextArea style={{ background: '#fdff9c93' }} rows={5} placeholder='Add an appointment note' />
                  </Form.Item>
                </div>
                <Form.Item noStyle name={'services'} />
              </DetailContainer>
              <TimeContainer>
                <Form.Item noStyle name={'date'} rules={[{ required: true }]}>
                  <CalendarPicker onMonthChange={onMonthChange} onPickDate={onPickDate} offDates={_dateOffState.data || []} />
                </Form.Item>
                <Text variant='H9' color='text_3' className='text-lg'>Start Time</Text>
                <Form.Item noStyle name={'startTime'} rules={[{ required: true }]}>
                  <StartTimes ref={startTimesRef} />
                </Form.Item>
                <Text mb={1} />
                <Form.Item noStyle name={'staff'}>
                  <BlockHourStaffs ref={blockHourStaffsRef} staffs={staffs} />
                </Form.Item>
              </TimeContainer>
            </Content>
          </Container>
        </Box>
      </FormStyled>
    </Modal>
  );
});
NewAppointmentVersionV2.displayName = 'NewAppointmentVersionV2';
export default NewAppointmentVersionV2;
const Container = styled.div`
min-height: 75vh;
display: flex;
flex-direction: column;
`;
const FormStyled = styled(Form)`
display: flex;
flex-direction: column;
zoom: 1;
@media (min-resolution: 0.8dppx) {
  zoom: 0.85;
}

.ant-form-item-explain-error {
  font-size: 20px;
}
`;
const Content = styled.div`
  flex: 1;
  display: flex;
  .divider {
    width: 1px;
    align-self: stretch;
    background: #CCD4DC;
  }
`;
const HeaderModal = styled.div`
background: rgb(35, 47, 62);
height: 4rem;
display: flex;
align-items: center;
justify-content: center;
span {
  color: #fff;
  text-align: center;
  font-family: Poppins;
  font-size: 30px;
  font-style: normal;
  font-weight: 600;
  line-height: 137.5%;
}
`;
const DetailContainer = styled.div`
flex: 1;
display: flex;
width: 1200px;
flex-direction: column;
border-right: 1px solid #86909C;
.WrapperInputSearch.container-input {
  padding-left: 0;
}

  .section-box {
    padding: 16px;
    border-bottom: 1px solid #86909C;
    background: #FFF;
    padding-bottom: 0px;
    .ant-form-item-label label {
      color: #1D2129;
      font-family: Poppins;
      font-size: 18px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }
  }

  .CustomerSearchInput {
    .ant-input-affix-wrapper {
      border-radius: 4px;
      border: 1px solid #CCD4DC;
      background: #F6F7FC;
      .ant-input-suffix svg {
        transform: scale(1.2);
      }
      input {
        background: transparent;
        color: #1D2129;
        font-family: Poppins;
        font-size: 20px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
      }
    }

    .ButtonAddCustomer {
      display: flex;
      width: 56px;
      height: 56px;
      padding: 10px;
      justify-content: center;
      align-items: center;
      gap: 10px;
      border-radius: 4px;
      background: #D2464F !important;
      border: 1px solid #D2464F !important;
    }
  }

  .ant-input {
    border: 1px solid #CCD4DC;
    background: #F6F7FC;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`;
const TimeContainer = styled.div`
width: 800px;
align-self: stretch;
display: flex;
flex-direction: column;
padding: 16px;
`;

